/**
 * ShareWithMe Component
 *
 * PURPOSE:
 * Manages and displays files and folders that have been shared with the current user.
 * Works in conjunction with sharedWithMeLoader to handle shared links and display shared items.
 *
 * KEY FEATURES:
 * - Displays all items shared with the current user when no specific link is provided
 * - Handles file preview for shared files (folders are handled by loader redirect)
 * - Provides search and filtering capabilities for shared items
 * - Manages permission levels for shared content
 * - Processes shared paths to navigate to matching shared folders
 *
 * DATA FLOW:
 * 1. Route loader (sharedWithMeLoader in DriveV2/index.tsx) processes the request:
 *    - Token present: Calls GetSharedLinkV2(token)
 *      • For folders: Directly redirects to /drive/folders/{path} (component never loads)
 *      • For files: Returns sharedLink data for component to handle preview
 *    - Path present: Returns sharedPath for component to match with user's shared items
 *    - Neither: Returns null values for general shared items view
 *
 * 2. Component receives loader data and handles:
 *    - sharedLink (file): Opens file preview using PresignedGetUrl
 *    - sharedPath: Matches with ListSharedWithMe results and navigates to folder
 *    - null values: Shows grid of all shared items
 *
 * NAVIGATION PATTERNS:
 * - Token-based folder access: /drive/shared?token={token}
 *   → Loader fetches link → Loader redirects to /drive/folders/{path}
 *   → ShareWithMe component never renders for shared folders with tokens
 *
 * - Token-based file access: /drive/shared?token={token}
 *   → Loader fetches link → Returns file data → Component handles preview
 *
 * - Path-based access: /drive/shared/{path}
 *   → Loader returns path → Component matches with shared items → Navigates to folder
 *
 * - General access: /drive/shared
 *   → Shows all shared items in grid view
 *
 * COMPONENT RESPONSIBILITIES:
 * - Fetches list of shared items via ListSharedWithMe API
 * - Handles file previews for shared files
 * - Matches shared paths with user's shared items
 * - Navigates to folders based on shared path matching
 * - Displays shared items in grid or list view
 * - Manages search/filter functionality
 *
 * RENDERING STATES:
 * 1. Loading: Shows spinner while fetching shared items from API
 * 2. Error: Displays error page for loader errors
 * 3. Folder not found: Shows 404 when shared path doesn't match any items
 * 4. Empty state: Shows "no results" when no items are shared
 * 5. Content view: Displays FileGrid with shared folders and files
 */

import { useMemo, useEffect, useState } from 'react'
import UserGroupIcons from '@/icons/UserGroupIcons'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    ListSharedWithMe,
    GetObjectMetadata,
    PresignedGetUrl,
} from '@/modules/Drive/services'
import useAuthStore from '@/stores/useAuthStore'
import useDriveStore from '@/stores/useDriveStore'
import {
    ObjectType,
    Folder,
    File as DriveFile,
    SharedLinkResponse,
} from '@/modules/Drive/types'
import ViewTypeSwitch from '../ViewTypeSwitch'
import FileGrid from '../FileGrid'
import { useTranslation } from 'react-i18next'
import {
    useLoaderData,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom'
import { AxiosError } from 'axios'
import ErrorPage from '@/components/ErrorBoundary/ErrorPage'
import { Loading } from 'vnetwork-ui'
import { ErrorResponse } from '@/types/shared'
import { useFilePreview } from '@/contexts/file-preview'
import { OFFICE_EXTENSIONS } from '@/hooks/useFilePreviewWithPresigned'
import { extractExtension } from '@/modules/Drive/utils'
import { toast } from 'react-hot-toast'
import { extractObjectName } from '../../helpers/helper'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const ShareWithMe = () => {
    const { t } = useTranslation()
    const employee = useAuthStore((state) => state.employee)
    const viewType = useDriveStore((state) => state.viewType)
    const setViewType = useDriveStore((state) => state.setViewType)
    const setCurrentPermission = useDriveStore(
        (state) => state.setCurrentPermission
    )
    const { pathname } = window.location

    const navigate = useNavigate()
    const location = useLocation()
    const { preview } = useFilePreview()
    const [searchParams] = useSearchParams()
    const searchQuery = searchParams.get('search')?.toLowerCase() ?? ''
    const {
        sharedLink,
        sharedPath,
        error: loaderError,
    } = useLoaderData() as {
        sharedLink: SharedLinkResponse | null
        sharedPath?: string | null
        error: AxiosError<ErrorResponse> | undefined
    }

    const [sharedFolders, setSharedFolders] = useState<Folder[]>([])
    const [sharedFiles, setSharedFiles] = useState<DriveFile[]>([])

    const [folderNotFound, setFolderNotFound] = useState<string | null>(null)

    // Get the list of shared with me objects
    const { data: sharedWithMe, isLoading: isGettingSharedWithMe } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SHARED_WITH_ME],
            queryFn: () => ListSharedWithMe(employee?.employee_id || 0),
            enabled: !!employee?.employee_id && !sharedLink,
            staleTime: 0,
        })

    const sharedItems = useMemo(
        () => sharedWithMe?.results || [],
        [sharedWithMe]
    )

    // Reset current permission when unmounting
    useEffect(() => {
        return () => {
            setCurrentPermission(undefined)
        }
    }, [])

    // Reset folderNotFound when sharedPath changes
    useEffect(() => {
        setFolderNotFound(null)
    }, [sharedPath])

    const handlePreview = async (objectKey: string) => {
        try {
            const response = await PresignedGetUrl(objectKey)
            let url = response.result
            if (
                extractExtension(objectKey) &&
                OFFICE_EXTENSIONS.some((ext) =>
                    extractExtension(objectKey).includes(ext)
                )
            ) {
                url = encodeURIComponent(url)
            }
            preview(url, url, {
                attachment_file_name: extractObjectName(objectKey),
                attachment_file_url: url,
            })
            window.history.replaceState(null, '', pathname)
        } catch (error) {
            console.error(error)
            toast.error(t('try-again'))
        }
    }

    // Navigate to the shared folder when the shared link is valid or when a specific path is provided
    useEffect(() => {
        // Handle navigation from shared link
        if (sharedLink?.share_object_type == ObjectType.FOLDER) {
            let objectKey = sharedLink?.share_object_key
            // Remove "pub/drives" prefix from the object key
            if (objectKey.startsWith('pub/drives/')) {
                objectKey = objectKey.substring('pub/drives/'.length)
            }

            // Navigate to drive/folders/{object_key} path
            navigate(`/drive/folders/${encodeURIComponent(objectKey)}`)
        } else if (sharedLink?.share_object_type == ObjectType.FILE) {
            console.log('File preview')
            handlePreview(sharedLink?.share_object_key)
            return
        }
        // Handle navigation from shared path
        else if (sharedPath && sharedWithMe?.results?.length) {
            // Find matching folder from shared items - try multiple matching strategies
            let matchingFolder = sharedWithMe.results.find(
                (item: any) =>
                    item.share_object_type === ObjectType.FOLDER &&
                    item.share_object_key.includes(sharedPath)
            )

            // If no exact match, try to find by exact path match
            if (!matchingFolder) {
                matchingFolder = sharedWithMe.results.find(
                    (item: any) =>
                        item.share_object_type === ObjectType.FOLDER &&
                        item.share_object_key === sharedPath
                )
            }

            // If still no match, try to find by ending with the sharedPath
            if (!matchingFolder) {
                matchingFolder = sharedWithMe.results.find(
                    (item: any) =>
                        item.share_object_type === ObjectType.FOLDER &&
                        item.share_object_key.endsWith(sharedPath)
                )
            }

            if (matchingFolder) {
                const folderPath = matchingFolder.share_object_key
                let objectKey = folderPath.endsWith('/')
                    ? folderPath.slice(0, -1)
                    : folderPath

                // Remove "pub/drives" prefix from the object key
                if (objectKey.startsWith('pub/drives/')) {
                    objectKey = objectKey.substring('pub/drives/'.length)
                }

                navigate(`/drive/folders/${encodeURIComponent(objectKey)}`)
            } else {
                // Folder not found - set error state
                setFolderNotFound(sharedPath)
            }
        }
        // Handle case where sharedPath is provided but no sharedWithMe results yet
        else if (sharedPath && sharedWithMe?.results?.length === 0) {
            setFolderNotFound(sharedPath)
        }
    }, [sharedLink, sharedPath, sharedWithMe, navigate, location.pathname])

    // Process shared with me data
    useEffect(() => {
        const processSharedWithMe = async () => {
            if (sharedWithMe?.results?.length) {
                const folders: Folder[] = []
                const filePromises: Promise<DriveFile | null>[] = []

                sharedWithMe.results.forEach((item: any) => {
                    if (item.share_object_type === ObjectType.FOLDER) {
                        folders.push({
                            Prefix: item.share_object_key,
                        } as Folder)
                    } else if (item.share_object_type === ObjectType.FILE) {
                        filePromises.push(
                            GetObjectMetadata(item.share_object_key)
                                .then((metadata) => ({
                                    Key: item.share_object_key,
                                    Size: metadata.result.size,
                                    LastModified: metadata.result.last_modified,
                                }))
                                .catch((error) => {
                                    console.log('error', error)
                                    return null
                                })
                        )
                    }
                })

                setSharedFolders(folders)

                // Wait for all file metadata to be fetched
                const files = (await Promise.all(filePromises)).filter(
                    Boolean
                ) as DriveFile[]
                setSharedFiles(files)
            } else {
                setSharedFolders([])
                setSharedFiles([])
            }
        }

        processSharedWithMe()
    }, [sharedWithMe])

    // Convert shared items to folders and files for FileGrid (fallback for simple display)
    const foldersAndFiles = useMemo(() => {
        const folders: Folder[] = []
        const files: DriveFile[] = []

        for (const item of sharedItems) {
            if (item.share_object_type === ObjectType.FOLDER) {
                folders.push({ Prefix: item.share_object_key })
            } else {
                files.push({
                    Key: item.share_object_key,
                    Size: 0, // Size not available in shared items
                    LastModified: item.share_link_created_at,
                })
            }
        }

        return { folders, files }
    }, [sharedItems])

    if (loaderError) {
        return (
            <ErrorPage
                status={loaderError.response?.status}
                error={loaderError}
            />
        )
    }

    if (isGettingSharedWithMe) {
        return <Loading.CircularProgress width={20} height={20} />
    }

    // Handle folder not found error - show 404 ErrorPage
    if (folderNotFound) {
        const notFoundError = new Error(
            t('drive.folder-not-shared', { folder: folderNotFound })
        )
        return <ErrorPage status={404} error={notFoundError} />
    }

    // Use processed data if available, otherwise fallback to simple conversion
    const displayFolders =
        sharedFolders.length > 0
            ? sharedFolders.filter((folder) =>
                  folder.Prefix.replace(/\/$/, '')
                      .split('/')
                      .pop()
                      ?.toLowerCase()
                      ?.includes(searchQuery)
              )
            : foldersAndFiles.folders.filter((folder) =>
                  folder.Prefix.replace(/\/$/, '')
                      .split('/')
                      .pop()
                      ?.toLowerCase()
                      ?.includes(searchQuery)
              )
    const displayFiles =
        sharedFiles.length > 0
            ? sharedFiles.filter((file) =>
                  file.Key.split('/')
                      .pop()
                      ?.toLowerCase()
                      ?.includes(searchQuery)
              )
            : foldersAndFiles.files.filter((file) =>
                  file.Key.split('/')
                      .pop()
                      ?.toLowerCase()
                      ?.includes(searchQuery)
              )

    if (sharedWithMe?.results == null) {
        return (
            <div className="flex flex-col gap-4">
                <header className="flex items-center justify-between">
                    <div className="font-semibold text-primary text-base flex items-center gap-3">
                        <UserGroupIcons className="w-5 h-5" />
                        <span>{t('shared-with-me-title')}</span>
                    </div>
                    <ViewTypeSwitch value={viewType} onChange={setViewType} />
                </header>
                <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500">{t('no-results')}</div>
                </div>
            </div>
        )
    }

    if (displayFolders.length > 0 || displayFiles.length > 0) {
        return (
            <div className="flex flex-col gap-4">
                <header className="flex items-center justify-between">
                    <div className="font-semibold text-primary text-base flex items-center gap-3">
                        <UserGroupIcons className="w-5 h-5" />
                        <span>{t('shared-with-me-title')}</span>
                    </div>
                    <ViewTypeSwitch value={viewType} onChange={setViewType} />
                </header>
                <FileGrid folders={displayFolders} files={displayFiles} />
            </div>
        )
    }

    return null
}

export default ShareWithMe
